import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LexdexLogo } from '../components/LexdexLogo';
import { useAuth } from '../contexts/AuthContext';
import CasesList from '../components/Cases/CasesList';
import CaseDetailView from '../components/Cases/CaseDetailView';
import ChatWidget from '../components/ChatWidget/ChatWidget';
import NewCaseModal from '../components/CaseModals/NewCaseModal';
import EditCaseModal from '../components/CaseModals/EditCaseModal';
import UploadDocumentModal from '../components/CaseModals/UploadDocumentModal';
import AddNoteModal from '../components/CaseModals/AddNoteModal';
import AddContactModal from '../components/CaseModals/AddContactModal';
import DocumentPreview from '../components/DocumentPreview/DocumentPreview';
import { useChat, useModals, useCases, useHomeHandlers } from '../hooks';
import { formatDate, formatDateTime, formatFileSize, formatCurrency } from '../utils';
import { Briefcase } from 'lucide-react';
import './Home.css';

const Home: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // Custom hooks for organized state management
  const cases = useCases();
  const chat = useChat(cases.selectedCaseId || undefined);
  const modals = useModals();

  // Event handlers
  const handlers = useHomeHandlers({ cases, modals, logout });

  // Profile menu state (local to Home page)
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <div className="home-container">
      {/* Header with logo and profile */}
      <header className="home-header">
        <div className="header-content">
          <div className="header-left"></div>
          <LexdexLogo showText={false} />
          <div className="header-right">
            <div className="profile-menu" ref={menuRef}>
              <button className="profile-button" onClick={toggleMenu}>
                <div className="profile-circle">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
              </button>
              {isMenuOpen && (
                <div className="profile-dropdown">
                  <div className="dropdown-header">
                    <div className="dropdown-name">{user?.name}</div>
                    <div className="dropdown-email">{user?.email}</div>
                  </div>
                  <div className="dropdown-divider"></div>
                  <button onClick={() => navigate('/profile')} className="dropdown-item">
                    Profile Settings
                  </button>
                  <button onClick={handlers.handleLogout} className="dropdown-item">
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-tabs">
          <button className={`sidebar-tab active`}>
            <Briefcase size={20} />
            <span>Cases</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="main-content">
        {cases.selectedCaseId && cases.caseDetail ? (
          cases.caseDetailLoading ? (
                  <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading case details...</p>
                  </div>
                ) : (
            <CaseDetailView
              caseDetail={cases.caseDetail}
              timeline={cases.timeline}
              teams={cases.teams}
              billingSummary={cases.billingSummary}
              billingItems={cases.billingItems}
              billingLoading={cases.billingLoading}
              activeTab={cases.activeTabDetail}
              onTabChange={cases.handleTabChangeDetail}
              onBack={cases.handleBackToCases}
              onEdit={handlers.handleEditCase}
              onAddNote={() => modals.setIsNoteModalOpen(true)}
              onAddContact={() => modals.setIsContactModalOpen(true)}
              onUploadDocument={() => modals.setIsUploadDocModalOpen(true)}
              onDownloadDocument={handlers.handleDownloadDocument}
              onPreviewDocument={handlers.handlePreviewDocument}
                                formatDate={formatDate}
              formatDateTime={formatDateTime}
              formatFileSize={formatFileSize}
              formatCurrency={formatCurrency}
            />
          )
        ) : (
          <CasesList
            cases={cases.cases}
            casesLoading={cases.casesLoading}
            casesError={cases.casesError}
            searchTerm={cases.searchTerm}
            setSearchTerm={cases.setSearchTerm}
            statusFilter={cases.statusFilter}
            setStatusFilter={cases.setStatusFilter}
            currentPage={cases.currentPage}
            hasMore={cases.hasMore}
            highestPage={cases.highestPage}
            onCaseClick={cases.handleCaseClick}
            onNewCase={() => modals.setIsNewCaseModalOpen(true)}
            onLoadCases={cases.loadCases}
            formatDate={formatDate}
          />
        )}
              </div>

      {/* Chat Widget */}
      <ChatWidget chat={chat} />

      {/* Modals */}
      <NewCaseModal
        isOpen={modals.isNewCaseModalOpen}
        newCaseData={modals.newCaseData}
        setNewCaseData={modals.setNewCaseData}
        isCreating={modals.isCreatingCase}
        onClose={() => modals.setIsNewCaseModalOpen(false)}
        onCreate={handlers.handleCreateCase}
      />

      <EditCaseModal
        isOpen={modals.isEditModalOpen}
        editData={modals.editData}
        setEditData={modals.setEditData}
        isUpdating={modals.isUpdating}
        onClose={() => modals.setIsEditModalOpen(false)}
        onUpdate={handlers.handleUpdateCase}
      />

      <UploadDocumentModal
        isOpen={modals.isUploadDocModalOpen}
        selectedFile={modals.selectedDocFile}
        isUploading={modals.isUploadingDoc}
        uploadProgress={modals.uploadDocProgress}
        onClose={() => modals.setIsUploadDocModalOpen(false)}
        onFileSelect={handlers.handleFileSelect}
        onUpload={handlers.handleUploadDocument}
        formatFileSize={formatFileSize}
      />

      <AddNoteModal
        isOpen={modals.isNoteModalOpen}
        noteData={modals.noteData}
        setNoteData={modals.setNoteData}
        isAdding={modals.isAddingNote}
        onClose={() => modals.setIsNoteModalOpen(false)}
        onAdd={handlers.handleAddNote}
      />

      <AddContactModal
        isOpen={modals.isContactModalOpen}
        contactData={modals.contactData}
        setContactData={modals.setContactData}
        isAdding={modals.isAddingContact}
        onClose={() => modals.setIsContactModalOpen(false)}
        onAdd={handlers.handleAddContact}
      />

      {/* Document Preview Modal */}
      {modals.previewDocument && (
        <DocumentPreview
          fileName={modals.previewDocument.fileName}
          fileType={modals.previewDocument.fileType}
          downloadUrl={modals.previewDocument.downloadUrl}
          caseId={cases.selectedCaseId || undefined}
          docId={modals.previewDocument.docId}
          onClose={() => modals.setPreviewDocument(null)}
        />
      )}
    </div>
  );
};

export default Home;
